import React, { useState, useRef, useEffect } from "react";
import { Modal, Input, Button, Typography, Space, message } from "antd";
import { LockOutlined, ReloadOutlined } from "@ant-design/icons";
import "./style.scss";
import { verify2FAReq } from "../../../utils/apis/2fa";
import logoIcon from "../../../assets/icons/acg-logo.png";

const { Title, Text } = Typography;

const TwoFactorAuthentication = ({
  visible,
  onCancel,
  tempToken,
  onSuccess,
}) => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Focus first input when modal opens
  useEffect(() => {
    if (visible && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0].focus();
      }, 100);
    }
  }, [visible]);

  // Ensure code state is always an array
  useEffect(() => {
    if (!Array.isArray(code)) {
      console.warn("Code state is not an array, resetting:", code);
      setCode(["", "", "", "", "", ""]);
    }
  }, [code]);

  // Handle input change
  const handleInputChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1); // Take only the last character
    }

    if (!/^\d*$/.test(value)) {
      return; // Only allow digits
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (index === 5 && value && newCode.every((digit) => digit !== "")) {
      handleVerify(newCode);
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setCode(newCode);
      setError("");

      // Auto-submit when complete code is pasted
      handleVerify(newCode);

      // Focus last input
      inputRefs.current[5]?.focus();
    }
  };
  const onVerify = async (code) => {
    setLoading(true);
    try {
      const response = await verify2FAReq({
        temp_token: tempToken,
        otp_code: code,
      });

      if (response.idToken) {
        // 2FA verification successful
        message.success("Authentication successful!");
        // Call success callback if provided
        if (onSuccess) {  
          onSuccess(response);
        }
        onCancel(); // Close modal on success
      } else {
        // Handle verification error
        setError(response.message || "Invalid 2FA code");
      }
    } catch (error) {
      console.error("2FA verification error:", error);
      const errorMessage =
        error.response?.data?.message || "2FA verification failed";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle verification
  const handleVerify = (codeArray = null) => {
    const codeToVerify = codeArray || code;

    // Safety check to ensure codeToVerify is an array
    if (!Array.isArray(codeToVerify)) {
      console.error("codeToVerify is not an array:", codeToVerify);
      setError("Invalid code");
      return;
    }

    const fullCode = codeToVerify.join("");

    if (fullCode.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    if (!/^\d{6}$/.test(fullCode)) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    onVerify(fullCode);
  };

  // Handle modal close
  const handleCancel = () => {
    setCode(["", "", "", "", "", ""]);
    setError("");
    onCancel();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={400}
      centered
      maskClosable={false}
      className="two-factor-auth-modal"
    >
      <div className="auth-content">
        <div className="auth-header">
          {/* <h1>
            {" "}
            <img src={logoIcon} alt="company-logo" />
            Alpha Futures
          </h1> */}
          <h2>Two-Factor Authentication</h2>
          <Text
            type="secondary"
            style={{
              textAlign: "center",
              display: "block",
              marginBottom: "24px",
            }}
          >
            Enter the 6-digit code from your authenticator app
          </Text>
        </div>

        <div className="code-input-container">
          <div className="code-inputs">
            {code.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                maxLength={1}
                size="large"
                className={`code-input ${digit ? "filled" : ""} ${
                  error ? "error" : ""
                }`}
                style={{
                  width: 50,
                  height: 50,
                  textAlign: "center",
                  fontSize: "18px",
                  fontWeight: "bold",
                  margin: "0 4px",
                }}
              />
            ))}
          </div>

          {error && (
            <Text
              type="danger"
              style={{ display: "block", textAlign: "start", marginTop: 8 }}
            >
              {error}
            </Text>
          )}
        </div>

        <div className="auth-actions">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button
              type="primary"
              size="large"
              onClick={handleVerify}
              loading={loading}
              disabled={
                !Array.isArray(code) || code.some((digit) => digit === "")
              }
              block
              icon={<LockOutlined />}
              className="standard-button"
              style={{
                background: "linear-gradient(135deg, #4bb390 0%, #3da883 100%)",
                border: "none",
                height: "44px",
                borderRadius: "8px",
                fontWeight: "500",
                fontSize: "16px",
              }}
            >
              Verify Code
            </Button>

            <Button
              type="text"
              onClick={handleCancel}
              disabled={loading}
              size="small"
              style={{
                color: "#a6a6a6",
                fontSize: "14px",
              }}
            >
              Cancel
            </Button>
          </Space>
        </div>
      </div>
    </Modal>
  );
};

export default TwoFactorAuthentication;
